/**
 * ArtemisMissionStages Component
 * Displays clickable hotspots at specific points along the Artemis trajectory
 * with tooltip functionality
 */

import { useMemo, useRef, useEffect } from 'react'
import { Html } from '@react-three/drei'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'

// 3D marker component for mission stages - renders HTML in 3D space (locked, no jitter)
export function MissionStageMarker({ 
  controlPoints, 
  trajectoryPercent,
  stageName,
  stageNumber,
  stageId,
  dotColor = '#EE3A17',
  bracketColor = '#E3E3E3',
  textColor = '#E3E3E3',
  blink = 1,
  reached = false,
  onClick,
  onPositionUpdate,
  isActive = false
}) {
  const markerRef = useRef(null)
  // Create curve from control points
  const curve = useMemo(() => {
    if (!controlPoints || controlPoints.length < 2) return null
    
    const curvePath = new THREE.CurvePath()
    
    for (let i = 0; i < controlPoints.length - 1; i++) {
      const p0 = controlPoints[i]
      const p1 = controlPoints[i + 1]
      
      const anchor0 = new THREE.Vector3(...p0.pos)
      const anchor1 = new THREE.Vector3(...p1.pos)
      const handle0Out = new THREE.Vector3(...p0.pos).add(new THREE.Vector3(...p0.handleOut))
      const handle1In = new THREE.Vector3(...p1.pos).add(new THREE.Vector3(...p1.handleIn))
      
      const curveSegment = new THREE.CubicBezierCurve3(
        anchor0,
        handle0Out,
        handle1In,
        anchor1
      )
      
      curvePath.add(curveSegment)
    }
    
    return curvePath
  }, [controlPoints])
  
  // Calculate position on the curve based on trajectory percentage
  const position = useMemo(() => {
    if (!curve) return [0, 0, 0]
    const t = trajectoryPercent / 100
    const point = curve.getPoint(t)
    return [point.x, point.y, point.z]
  }, [curve, trajectoryPercent])
  
  // Update screen position every frame if this marker is active
  useFrame(() => {
    if (isActive && markerRef.current && onPositionUpdate) {
      const rect = markerRef.current.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2
      onPositionUpdate(stageId, centerX, centerY)
    }
  })
  
  if (!curve) return null
  
  return (
    <group position={position}>
      <Html
        sprite
        zIndexRange={[0, 0]}
        style={{
          pointerEvents: onClick ? 'auto' : 'none',
          userSelect: 'none'
        }}
      >
        <div
          ref={markerRef}
          className="mission-stage-marker"
          onClick={(e) => {
            if (onClick) {
              const rect = e.currentTarget.getBoundingClientRect()
              const centerX = rect.left + rect.width / 2
              const centerY = rect.top + rect.height / 2
              onClick(stageId, stageName, centerX, centerY, e)
            }
          }}
          style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: reached ? '#E3E3E3' : dotColor,
            boxShadow: `0 0 24px ${reached ? '#E3E3E3' : dotColor}`,
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'monospace',
            color: reached ? '#000000' : '#E3E3E3',
            cursor: onClick ? 'pointer' : 'default',
            transition: 'transform 0.2s ease'
          }}
          onMouseEnter={(e) => { if (onClick) e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.1)' }}
          onMouseLeave={(e) => { if (onClick) e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)' }}
        >
          {stageNumber}
        </div>
      </Html>
    </group>
  )
}

export default MissionStageMarker
